import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  ITrajectoryDetail,
  TrajectorySectionCode,
  trajectorySectionCodeLookup,
} from '../../../../config/api/models/Trajectory';
import { useI18nPath } from '../../../../config/clientRoutes/hooks';
import { Loading, Tooltip } from '../../../shared/components';
import { useFullBook, useUser } from '../../../shared/hooks';
import {
  NO_SECTION_FOUND_ERROR,
  NoSectionFoundError,
  SECTION_CODE_PENDING,
  usePagesByType,
  useSectionCodeToLoad,
} from '../../hooks';
import { EmptyTrajectory, TrajectoryToolbox } from '../TrajectoryDetail';
import { DifferentiationIndicator } from '../Differentiation';

import TrajectoryViewSelectedSection from './TrajectoryViewSelectedSection';
import { LogActionTypes, useDataLogging } from '../../../shared/data-logging';
import { TeacherFiltersEnum } from '../../../shared/constants';
import { UrlParams } from '../../../shared/types';
import { FeedbackModule } from '../../../feedback';
import { useDisclosure } from 'react-use-disclosure';
import { TrajectoryEditableTitle } from '../TrajectoryDetail/TrajectoryEditableTitle';
import { isValidSectionCode } from '../../helpers';
import { useAppDispatch, useAppSelector } from '../../../shared/hooks/useAppDispatch';
import {
  FullscreenWrapper,
  PageFeedbackIndicator,
  PageHeader,
  StyledPageTitle,
  Wrapper,
} from './Atoms';
import TrajectorySubHeader from 'src/modules/shared/components/SubHeader/TrajectorySubheader/TrajectorySubHeader';
import { useMediaQuery } from '@material-ui/core';
import theme from 'src/config/theme';
import TrajectoryViewDetailInfo from './TrajectoryViewDetailInfo';
import { setGeogebraAppName } from 'src/modules/shared/slices/geogebra/entity/geogebraSlice';
import {
  setSidebarOpen,
  setSidebarTabsVisible,
} from 'src/modules/shared/slices/trajectorySidebar/entity/trajectorySidebarSlice';
import { setPageWidth } from 'src/modules/shared/slices/privatePageSettings/entity/privatePageSettingsSlice';
import { setCurrentSectionCode } from 'src/modules/shared/slices/trajectories/entity/trajectorySlice';
import { openSidePanel } from 'src/modules/shared/slices/sidePanel/sidePanelSlice';
import { PresentationMode } from '../PresentationMode/PresentationMode';
import StudyCard from 'src/modules/study-card/StudyCard';
import { setBlockToScroll } from 'src/modules/shared/slices/blocks/entity/BlocksSlice';
import { VideoPageHelper } from 'src/components/VideoPageHelper/VideoPageHelper';
import { PlataformGuidePlace } from 'src/store/plataform-guide/types';
import { useRequestMeMutation } from 'src/modules/shared/slices/auth/entity/api/auth';

type Props = {
  trajectory: ITrajectoryDetail;
  refetchTrajectoryDetailsCallback: () => void;
  onPublish?: () => void;
  onUnPublish?: () => void;
};

const TrajectoryViewDetail: FC<Props> = ({
  trajectory,
  refetchTrajectoryDetailsCallback,
  onPublish,
  onUnPublish,
}) => {
  // hooks
  const { t } = useTranslation();
  const { getPath } = useI18nPath();
  const history = useHistory();
  const log = useDataLogging();
  const location = useLocation();
  const fullBook = useFullBook();
  const { isAuthor, isTeacher, isStudent } = useUser();
  const { bookId, chapterId, trajectoryId, sectionCode, seriesId } = useParams<UrlParams>();
  const pages = usePagesByType(trajectorySectionCodeLookup[sectionCode as TrajectorySectionCode]);
  const currentPageId = useMemo(() => pages?.data?.[0]?.id ?? seriesId, [pages, seriesId]);
  const {
    isOpen: pageFeedbackIsOpen,
    close: closePageFeedback,
    toggle: togglePageFeedback,
    open: openPageFeedback,
  } = useDisclosure(false);
  const dispatch = useAppDispatch();

  const [, { data: meResponse, isSuccess }] = useRequestMeMutation({
    fixedCacheKey: '/me',
  });

  // redux
  const activeFilter = useAppSelector((state) => state.filters.activeFilter);
  const selectedGroup = useAppSelector((state) => state.filters.selectedGroup);
  const viewMode = useAppSelector((state) => state.trajectories.viewMode);
  // need to be after viewMode declaration
  const sectionCodeToLoad = useSectionCodeToLoad(trajectory, viewMode, sectionCode);
  const isSidePanelOpen = useAppSelector((state) => state.sidepanel.isOpen);
  const fullscreenMode = useAppSelector((state) => state.fullscreenMode.fullscreenMode);
  const showPresentationMode = useAppSelector(
    (state) => state.presentationMode.showPresentationMode
  );
  const blockToScroll = useAppSelector((state) => state.blocks.blockToScroll);
  const fullscreenScaleFactor = useAppSelector(
    (state) => state.fullscreenMode.fullscreenScaleFactor
  );

  // state
  const [sectionToShowAfterReFetch, setSectionToShowAfterReFetch] = useState<
    TrajectorySectionCode | ''
  >('');

  // mediaquery
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const feedbackEnabled = useMemo(
    () =>
      (isTeacher && activeFilter === TeacherFiltersEnum.FOLLOW_UP && selectedGroup !== '') ||
      isStudent,
    [isTeacher, isStudent, activeFilter, selectedGroup]
  );

  useEffect(() => {
    dispatch(setPageWidth());
  }, [dispatch]);

  useEffect(() => {
    if (!location.state) return;
    if (location.state && !(location.state as any).feedbackPageId) return;
    if ((location.state as any).feedbackBlockId != null) return;
    if ((location.state as any).feedbackPageId === currentPageId) {
      openPageFeedback();

      setTimeout(() => {
        const indicator = document.querySelector(`#feedback_${currentPageId}`);
        if (indicator)
          indicator.scrollIntoView({
            block: 'center',
            inline: 'nearest',
            behavior: 'smooth',
          });
      }, 100);
    }
  }, [location, currentPageId, openPageFeedback]);

  useEffect(() => {
    if (!blockToScroll) return;
    if (showPresentationMode) return;
    const indicator = document.getElementById(blockToScroll);
    if (indicator) {
      indicator.scrollIntoView({
        block: 'center',
        inline: 'nearest',
        behavior: 'smooth',
      });

      dispatch(setBlockToScroll(null));
    }
  }, [blockToScroll, dispatch, showPresentationMode]);

  useEffect(() => {
    if (
      sectionCodeToLoad != null &&
      sectionCodeToLoad !== SECTION_CODE_PENDING &&
      sectionCodeToLoad !== NO_SECTION_FOUND_ERROR
    ) {
      log(LogActionTypes.NAVIGATION_BOOKS_TRAJECTORY, {
        bookId,
        chapterId,
        trajectoryId,
      });
    }
  }, [log, chapterId, trajectoryId, bookId, sectionCodeToLoad]);

  const pageTitle = useMemo(() => {
    const title: string = trajectory.parentTitle ?? trajectory.title;
    const sectionTitle = isValidSectionCode(sectionCode as string)
      ? t(
          `trajectory.sections.${
            trajectorySectionCodeLookup[sectionCode as TrajectorySectionCode]
          }.title`
        )
      : null;

    return (
      <Tooltip title="Add" placement="top">
        <p>{sectionTitle ? `${title} - ${sectionTitle}` : title}</p>
      </Tooltip>
    );
  }, [trajectory, sectionCode, t]);

  const handleSelectedSectionChange = useCallback(
    (newSectionCode: TrajectorySectionCode | NoSectionFoundError) => {
      dispatch(openSidePanel(false));
      let path = getPath(isAuthor ? 'trajectory' : 'trajectoryView', {
        bookId,
        chapterId,
        trajectoryId,
        'sectionCode?': newSectionCode === NO_SECTION_FOUND_ERROR ? undefined : newSectionCode,
        'sectionName?':
          newSectionCode === NO_SECTION_FOUND_ERROR || newSectionCode == null
            ? undefined
            : t(
                `trajectory.sections.${
                  trajectorySectionCodeLookup[newSectionCode as TrajectorySectionCode]
                }.title`
              ).toLowerCase(),
        'seriesId?':
          sectionCode !== newSectionCode
            ? undefined
            : newSectionCode === '2' || newSectionCode === '3'
            ? seriesId
            : undefined,
      });

      // if no section is found, strip the empty slashes from the path so the url is cleaner
      if (path.substring(-3, 3) === '///') {
        path = path.substring(0, path.length - 3);
      }
      dispatch(setCurrentSectionCode(sectionCode as TrajectorySectionCode));

      if (history.location.pathname !== path) {
        refetchTrajectoryDetailsCallback();
        history.push(path, {
          blockId: (history.location.state as { blockId: string })?.blockId,
          addExerciseOrEvaluation:
            (newSectionCode === '2' || newSectionCode === '3') && viewMode === 'edit',
          feedbackPageId: (history.location.state as { feedbackPageId: string })?.feedbackPageId,
          feedbackBlockId: (history.location.state as { feedbackBlockId: string })?.feedbackBlockId,
        });
      }
    },
    [
      getPath,
      isAuthor,
      bookId,
      chapterId,
      trajectoryId,
      t,
      sectionCode,
      seriesId,
      history,
      refetchTrajectoryDetailsCallback,
      viewMode,
      dispatch,
    ]
  );

  useEffect(() => {
    // close sidebar when switching between trajectories (differentiations)
    dispatch(setSidebarOpen(false));
    // close calculator when switching between trajectories
    dispatch(setGeogebraAppName(''));
  }, [trajectoryId, dispatch]);

  useEffect(() => {
    if (!sectionCodeToLoad && sectionCodeToLoad === NO_SECTION_FOUND_ERROR) return;
    if (sectionCodeToLoad === SECTION_CODE_PENDING) return;

    handleSelectedSectionChange(sectionCodeToLoad as TrajectorySectionCode);
  }, [handleSelectedSectionChange, sectionCode, sectionCodeToLoad]);

  useEffect(() => {
    if (sectionToShowAfterReFetch !== '') {
      handleSelectedSectionChange(sectionToShowAfterReFetch);
      setSectionToShowAfterReFetch('');
    }
  }, [
    trajectoryId,
    trajectory,
    sectionToShowAfterReFetch,
    setSectionToShowAfterReFetch,
    handleSelectedSectionChange,
  ]);

  const handlePatchProgress = useCallback(
    (nextSectionCode: TrajectorySectionCode) => {
      setSectionToShowAfterReFetch(nextSectionCode);
      refetchTrajectoryDetailsCallback();
    },
    [setSectionToShowAfterReFetch, refetchTrajectoryDetailsCallback]
  );

  const getPlaceForPlatformGuide = (): PlataformGuidePlace => {
    if (sectionCodeToLoad === '0') {
      return PlataformGuidePlace.Opbouw;
    }
    if (sectionCodeToLoad === '1') {
      return PlataformGuidePlace.Theory;
    }
    if (sectionCodeToLoad === '2') {
      return PlataformGuidePlace.Oefenen;
    }
    if (sectionCodeToLoad === '3') {
      return PlataformGuidePlace.Studeren;
    }
    return PlataformGuidePlace.Opbouw;
  };

  const [showNoPrintMessage, setShowNoPrintMessage] = useState<boolean>(false);
  useEffect(() => {
    window.addEventListener('beforeprint', () => {
      setShowNoPrintMessage(true);
    });
    return () => window.removeEventListener('beforeprint', () => {});
  }, []);

  useEffect(() => {
    window.addEventListener('afterprint', () => {
      setShowNoPrintMessage(false);
    });
    return () => window.removeEventListener('afterprint', () => {});
  }, []);

  return !!sectionCodeToLoad && sectionCodeToLoad !== SECTION_CODE_PENDING ? (
    <>
      {isTeacher && !meResponse?.stepper?.key && isSuccess && (
        <VideoPageHelper place={getPlaceForPlatformGuide()} />
      )}
      {sectionCodeToLoad === NO_SECTION_FOUND_ERROR ? (
        <>
          {isAuthor ? (
            <Wrapper>
              <PageHeader>
                <StyledPageTitle prefix={undefined} marginBottom={isTeacher ? 1 : 3}>
                  {pageTitle}
                </StyledPageTitle>
                <DifferentiationIndicator trajectory={trajectory} />
              </PageHeader>
              <EmptyTrajectory viewMode={viewMode} />
            </Wrapper>
          ) : (
            <div>{t('trajectory.empty.warning')}</div>
          )}
        </>
      ) : sectionCodeToLoad === '3' ? (
        <StudyCard trajectory={trajectory} />
      ) : (
        <>
          <Wrapper
            id="trajectory-view-detail-wrapper"
            className={`${showNoPrintMessage ? 'hide-on-print' : ''}`}
          >
            <FullscreenWrapper factor={fullscreenScaleFactor}>
              {showPresentationMode && <PresentationMode />}
              {!showPresentationMode && (
                <>
                  <PageHeader>
                    {trajectory.number !== undefined && sectionCode !== undefined && (
                      <TrajectoryViewDetailInfo
                        trajectoryNumber={trajectory.number}
                        trajectoryTitle={trajectory.title}
                        sectionCode={sectionCode}
                      />
                    )}
                    <TrajectoryEditableTitle
                      bookId={bookId as string}
                      trajectory={trajectory}
                      onRenameTrajectory={refetchTrajectoryDetailsCallback}
                      viewMode={viewMode}
                    />
                    {(isTeacher || isAuthor) && (
                      <DifferentiationIndicator trajectory={trajectory} />
                    )}
                    {feedbackEnabled && currentPageId != null && (
                      <PageFeedbackIndicator pageId={currentPageId} onClick={togglePageFeedback} />
                    )}
                  </PageHeader>
                  {currentPageId != null && (
                    <FeedbackModule
                      pageId={currentPageId}
                      context="page"
                      mode="floating"
                      open={feedbackEnabled && pageFeedbackIsOpen}
                      onClose={closePageFeedback}
                    />
                  )}
                  <TrajectoryViewSelectedSection
                    sectionCode={sectionCode as TrajectorySectionCode}
                    onSelectedSectionChange={handleSelectedSectionChange}
                    hasTheory={trajectory.pages.some(
                      (page) => page.type === 'theory' && page.hasContent
                    )}
                    hasExercises={trajectory.pages.some(
                      (page) => page.type === 'worksheet_exercises' && page.hasContent
                    )}
                    progress={trajectory.progress}
                    viewMode={viewMode}
                    onPatchProgress={handlePatchProgress}
                  />
                </>
              )}
            </FullscreenWrapper>

            {!!fullBook.data && !isMobile && (
              <TrajectoryToolbox
                mode={viewMode}
                sectionCode={sectionCode as TrajectorySectionCode}
                fullBook={fullBook.data}
                isTrajectoryInDefaultBook={fullBook.data.isDefault}
                pageId={currentPageId}
                onOpenSidebar={() => {
                  dispatch(setSidebarOpen(true));
                  dispatch(setSidebarTabsVisible(true));
                }}
                fullscreen={fullscreenMode}
              />
            )}
          </Wrapper>
          {
            <div style={{ display: showNoPrintMessage ? 'block' : 'none' }}>
              Je kunt de browserprint niet gebruiken, maar als je een licentie voor Lernova hebt,
              krijg je toegang tot mooi opgemaakte en printklare pdf’s, zodat je leerlingen op
              papier kunnen noteren en studeren.
            </div>
          }
        </>
      )}

      {trajectory && (
        <TrajectorySubHeader
          bookId={bookId}
          trajectory={trajectory}
          onSelectedSectionChange={handleSelectedSectionChange}
          pageId={currentPageId}
          className={
            fullscreenMode ? 'fullscreen' : isSidePanelOpen ? 'side-panel-open' : undefined
          }
        />
      )}
    </>
  ) : (
    <Loading />
  );
};

export default TrajectoryViewDetail;
